// Timer hook for the BMD test-taking flow (spec §4.4):
//   * Starts a countdown from `mainSeconds`, defaulting to
//     BMD_MCQ_TIMER_SECONDS.
//   * When main hits 0, automatically transitions to a `bufferSeconds`
//     countdown (default 10s) as a grace period.
//   * When the buffer hits 0, fires `onTimeout` exactly once and the timer is
//     EXPIRED. The orchestrator is expected to auto-skip the question with
//     skip_reason = 'TIMEOUT' and advance.
//   * Supports pause/resume (e.g. pause while the student answers follow-up
//     questions after picking an MCQ answer).
//   * `reset()` returns to the main countdown fresh, for the next question.
//
// The hook owns all timing logic; the <BmdTimer> component is display-only.

import { useCallback, useEffect, useRef, useState } from "react"
import {
  BMD_MCQ_BUFFER_SECONDS,
  BMD_MCQ_TIMER_SECONDS,
} from "../../constants/diagnostics/bmd.constants"

export type BmdTimerPhase = "MAIN" | "BUFFER" | "EXPIRED"

export type UseBmdTimerParams = {
  mainSeconds?: number
  bufferSeconds?: number
  autoStart?: boolean
  onTimeout?: () => void
}

export type UseBmdTimerState = {
  remainingSeconds: number
  phase: BmdTimerPhase
  isPaused: boolean
  isExpired: boolean
  pause: () => void
  resume: () => void
  reset: () => void
}

export function useBmdTimer({
  mainSeconds = BMD_MCQ_TIMER_SECONDS,
  bufferSeconds = BMD_MCQ_BUFFER_SECONDS,
  autoStart = true,
  onTimeout,
}: UseBmdTimerParams = {}): UseBmdTimerState {
  const [remainingSeconds, setRemainingSeconds] = useState<number>(mainSeconds)
  const [phase, setPhase] = useState<BmdTimerPhase>("MAIN")
  const [isPaused, setIsPaused] = useState<boolean>(!autoStart)

  // Keep onTimeout fresh across renders without restarting the interval.
  const onTimeoutRef = useRef<(() => void) | undefined>(onTimeout)
  useEffect(() => {
    onTimeoutRef.current = onTimeout
  }, [onTimeout])

  // Guard: ensure onTimeout fires exactly once per lifecycle (re-armed on reset).
  const hasFiredRef = useRef<boolean>(false)

  const pause = useCallback(() => setIsPaused(true), [])
  const resume = useCallback(() => {
    if (phase !== "EXPIRED") setIsPaused(false)
  }, [phase])

  const reset = useCallback(() => {
    setRemainingSeconds(mainSeconds)
    setPhase("MAIN")
    setIsPaused(!autoStart)
    hasFiredRef.current = false
  }, [mainSeconds, autoStart])

  useEffect(() => {
    if (isPaused || phase === "EXPIRED") return

    const intervalId = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev > 1) return prev - 1

        // Landed on 0, so transition based on the current phase.
        if (phase === "MAIN") {
          // A zero buffer means "expire the moment the main countdown ends",
          // which is what the follow-up timer wants. Without this it would
          // spend an extra tick sitting in BUFFER at 0.
          if (bufferSeconds > 0) {
            setPhase("BUFFER")
            return bufferSeconds
          }
          setPhase("EXPIRED")
          if (!hasFiredRef.current) {
            hasFiredRef.current = true
            onTimeoutRef.current?.()
          }
          return 0
        }

        // phase is "BUFFER" and the count hit 0, so EXPIRED.
        setPhase("EXPIRED")
        if (!hasFiredRef.current) {
          hasFiredRef.current = true
          onTimeoutRef.current?.()
        }
        return 0
      })
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [isPaused, phase, bufferSeconds])

  return {
    remainingSeconds,
    phase,
    isPaused,
    isExpired: phase === "EXPIRED",
    pause,
    resume,
    reset,
  }
}
